import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardHeader,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FormikProps } from 'formik';
import {
  Brand,
  ProductCreateWithVariants,
  ProductUpdateWithVariants,
} from 'src/app/api/openapi';
import CreateVariantsTable from './CreateVariantsTable';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ProductMedia from './ProductMedia';
import { RichTreeViewPro, useTreeViewApiRef } from '@mui/x-tree-view-pro';
import {
  getChildCategories,
  getParentCategories,
  searchCategories,
} from 'src/app/api/hooks/categories';
import { BrandClient } from 'src/app/api/clients/brands';

const CreateProductForm = ({
  values,
  setFieldValue,
  submitForm,
  getFieldProps,
}: FormikProps<ProductCreateWithVariants | ProductUpdateWithVariants>) => {
  const { orgId, productId } = useParams();
  const navigate = useNavigate();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [selectedBrand, setSelectedBrands] = useState<Brand | null>(
    values.brand as Brand
  );

  const [categorySearchResults, setCategorySearchResults] = useState<any[]>([
    { id: -1, label: 'Show Parent Categories' },
  ]);
  const [parentCategories, setParentCategories] = useState<any[]>([]);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    getParentCategories().then((items) => setParentCategories(items || []));
    if (orgId)
      BrandClient.listBrands(orgId).then(({ data }) =>
        setBrands(data.items || [])
      );
  }, []);
  const handleSearchCategories = (query: string) =>
    searchCategories(query).then((items) =>
      setCategorySearchResults(items || [])
    );

  const apiRef = useTreeViewApiRef();

  const ADULT_CONSIDERATIONS = [
    { label: 'Alcohol', value: 'AlcoholConsideration' },
    {
      label: 'Dangerous Good',
      value: 'DangerousGoodConsideration',
    },
    { label: 'Healthcare', value: 'HealthcareConsideration' },
    { label: 'Narcotic', value: 'NarcoticConsideration' },
    {
      label: 'Reduced Relevance For Children',
      value: 'ReducedRelevanceForChildrenConsideration',
    },
    {
      label: 'Sexual Content',
      value: 'SexualContentConsideration',
    },
    {
      label: 'Tobacco and Nicotine',
      value: 'TobaccoNicotineConsideration',
    },
    {
      label: 'Unclassified Adult Consideration',
      value: 'UnclassifiedAdultConsideration',
    },
    { label: 'Violence', value: 'ViolenceConsideration' },
    { label: 'Weapon', value: 'WeaponConsideration' },
    { label: 'None', value: 'null' },
  ];

  return (
    <>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h1">
          {productId ? 'Edit' : 'Create'} Product
        </Typography>
        <Box>
          <Button variant="text" onClick={() => navigate(-1)} sx={{ mr: 2 }}>
            Cancel
          </Button>
          <Button variant="flat" onClick={submitForm}>
            {productId ? 'Save' : 'Create'}
          </Button>
        </Box>
      </Stack>
      <ProductMedia product={values} setFieldValue={setFieldValue} />
      <Stack component="form" alignItems="center" width="100%">
        <TextField
          label="Title"
          {...getFieldProps('title')}
          sx={{ m: 1, width: '100%' }}
        />
        <TextField
          label="Subtitle"
          {...getFieldProps('subtitle')}
          sx={{ m: 1, width: '100%' }}
        />
        <Card sx={{ width: '100%', m: 1, p: 3 }}>
          <CardHeader title="Description" sx={{ p: 0 }} />
          <TextField
            {...getFieldProps('description')}
            multiline
            minRows={3}
            sx={{ width: '100%', mt: 1 }}
          />
        </Card>
        <Stack
          direction="row"
          justifyContent="space-between"
          mb={2}
          width="100%"
        >
          <Stack width="60%">
            <Autocomplete
              options={brands}
              getOptionLabel={(option) => option.name || ''}
              value={selectedBrand}
              renderInput={(params) => (
                <TextField {...params} label="Brand" sx={{ m: 1 }} />
              )}
              onChange={(_, value) => {
                setSelectedBrands(value);
                setFieldValue('brand', value);
              }}
            />
            <Autocomplete
              placeholder="Type to search..."
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search for categories"
                  label="Category"
                  sx={{ m: 1 }}
                />
              )}
              value={values.google_product_category || null}
              onChange={(_e, value: any) =>
                setFieldValue('google_product_category', value)
              }
              onInputChange={async (_event, searchTerm, reason) => {
                if (reason === 'reset') return;
                if (searchTerm) {
                  setIsSearching(true);
                  handleSearchCategories(searchTerm).finally(() =>
                    setIsSearching(false)
                  );
                  return;
                }
                setCategorySearchResults([
                  { id: -1, label: 'Show Parent Categories' },
                ]);
              }}
              options={categorySearchResults}
              filterOptions={(v) => v}
              getOptionLabel={(option: any) => option?.label || option?.name}
              renderOption={(props, option: any, state) => {
                if (option.id === -1)
                  return (
                    <RichTreeViewPro
                      apiRef={apiRef}
                      key={option.id}
                      items={parentCategories}
                      dataSource={{
                        getChildrenCount: (item) => item.childrenCount,
                        getTreeItems: getChildCategories,
                      }}
                      checkboxSelection
                      selectedItems={values.google_product_category?.id || null}
                      onSelectedItemsChange={(_, itemId) =>
                        setFieldValue(
                          'google_product_category',
                          itemId ? apiRef.current?.getItem(itemId) : undefined
                        )
                      }
                      sx={{
                        '.MuiTreeItem-content': {
                          py: 1,
                        },
                      }}
                    />
                  );
                const { inputValue } = state;
                const parts: string[] =
                  option?.label?.split(new RegExp(`(${inputValue})`, 'gi')) ||
                  [];
                return (
                  <Stack component="li" {...props}>
                    <Typography variant="body1" textAlign="left" width="100%">
                      {parts.map((part, i) => (
                        <Box
                          component="span"
                          // eslint-disable-next-line react/no-array-index-key
                          key={`${part}-${i}`}
                          sx={
                            part.toLowerCase() === inputValue.toLowerCase()
                              ? { fontWeight: 700 }
                              : undefined
                          }
                        >
                          {part}
                        </Box>
                      ))}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      textAlign="left"
                      width="100%"
                    >
                      {option.displayText}
                    </Typography>
                  </Stack>
                );
              }}
              open={popoverOpen}
              onOpen={() => setPopoverOpen(true)}
              onClose={(_, reason) =>
                reason !== 'blur' && setPopoverOpen(false)
              }
              loading={isSearching}
              loadingText="Searching..."
              noOptionsText="Type to search"
              fullWidth
              disableCloseOnSelect
              slotProps={{
                paper: {
                  sx: {
                    '.MuiAutocomplete-listbox': { maxHeight: '65vh' },
                  },
                },
              }}
            />
            <TextField
              label="Adult Considerations"
              select
              {...getFieldProps('has_adult_consideration')}
              value={values.has_adult_consideration || 'null'}
              sx={{ m: 1, width: '100%' }}
            >
              {ADULT_CONSIDERATIONS.map((item) => (
                <MenuItem value={item.value} key={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack sx={{ flexGrow: 1, p: 2, m: 2 }}>
            <FormControlLabel
              label={<Typography fontWeight={700}>EBT Eligible</Typography>}
              control={
                <Checkbox
                  {...getFieldProps('is_ebt_eligible')}
                  checked={values.is_ebt_eligible}
                />
              }
            />
            <FormControlLabel
              label={<Typography fontWeight={700}>Published</Typography>}
              control={
                <Checkbox
                  {...getFieldProps('is_published')}
                  checked={values.is_published}
                />
              }
            />
          </Stack>
        </Stack>
        <CreateVariantsTable
          values={values}
          setFieldValue={setFieldValue}
          getFieldProps={getFieldProps}
        />
      </Stack>
    </>
  );
};

export default CreateProductForm;
